import { FC, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipGroup, TooltipGroupAnchor } from '@/components/shared/tooltip/TooltipGroup';
import { CopyElementIcon } from '@/components/base/Icons/CopyElementIcon';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import useCopyMessage from '../../useCopyMessage';
import { TestIds } from './testIds';
import styles from './UserBubble.module.css';

export interface UserBubbleProps {
  children: ReactNode;
  timestamp?: string;
}

const UserBubble: FC<UserBubbleProps> = ({ children, timestamp }) => {
  const { t } = useTranslation();
  const { handleCopy, handlePointerEnter, isCopied } = useCopyMessage(children);
  const tooltipGroupId = useId();
  const copyTooltipText = isCopied ? t('global.copyToClipboard') : t('components.aiChatDrawer.userBubble.copyTooltip');

  return (
    <div
      className={styles.container}
      aria-label={t('components.aiChatDrawer.userBubble.ariaLabel')}
      data-testid={TestIds.bubble}
    >
      <div className={styles.messageRow}>
        <TooltipGroup id={`user-bubble-${tooltipGroupId}`} shouldShowForTruncatedOnly={false} delayShow={200}>
          <TooltipGroupAnchor value={copyTooltipText} place="top" forceDisplayTooltip>
            <span className={styles.copyTooltipAnchor}>
              <IconButton
                appearance="text"
                size="S"
                icon={<CopyElementIcon className={styles.copyIcon} />}
                className={styles.copyButton}
                onClick={handleCopy}
                onMouseEnter={handlePointerEnter}
                aria-label={t('components.aiChatDrawer.userBubble.copyAriaLabel')}
                data-testid={TestIds.copyButton}
              />
            </span>
          </TooltipGroupAnchor>
        </TooltipGroup>

        <div className={styles.bubble} data-testid={TestIds.message}>
          <Text as="span" className={styles.text}>
            {children}
          </Text>
        </div>
      </div>
      {timestamp && (
        <Text as="small" className={styles.timestamp} data-testid={TestIds.timestamp}>
          {timestamp}
        </Text>
      )}
    </div>
  );
};

export default UserBubble;
